import DeleteIcon from '@mui/icons-material/Delete';
import {
    Autocomplete,
    Button,
    Chip,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Modal,
    ModalDialog,
    Tooltip,
    Typography,
} from '@mui/joy';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useGlobalContext } from '../../Context';
import { Core } from '../../utilities/core';
import { Metadata } from '../../utilities/metadata/index';

type KickoffSelectorProps = {
    kickoff: string;
    setKickoff: (val: string) => void;
};

const KickoffSelector: React.FC<KickoffSelectorProps> = ({ kickoff, setKickoff }) => {
    const { metadata, core, setMetadata } = useGlobalContext();

    // Locked remarks that should always appear at the top
    const lockedRemarks = ['FL-ADYEN', 'FL-CREW', 'FL-EXTERNAL', 'FL-FORTRESS', 'FREEFLIGHT', 'PR', 'RESEARCH'];

    const [kickoffOptions, setKickoffOptions] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [pendingValue, setPendingValue] = useState('');

    useEffect(() => {
        if (metadata?.kickOffs) {
            const metadataOptions = metadata.kickOffs.map((a) => a.code);
            // Filter out locked remarks from metadata to avoid duplicates
            const nonLockedOptions = metadataOptions.filter(opt => !lockedRemarks.includes(opt));
            // Combine locked remarks at the top with other options
            setKickoffOptions([...lockedRemarks, ...nonLockedOptions]);
        } else {
            // If no metadata, just show locked remarks
            setKickoffOptions(lockedRemarks);
        }
    }, [metadata]);

    const handleAddNew = async () => {
        const trimmed = pendingValue.trim();
        if (trimmed && !kickoffOptions.includes(trimmed)) {
            setKickoffOptions((prev) => [...prev, trimmed]);
            setKickoff(trimmed);
        }

        if (core) {
            await Metadata.putItem(core, 'finance/travel-kickoff', trimmed);
            const updated = await Metadata.retrieveMetadata(core);
            setMetadata(updated);
        }
        setOpenModal(false);
        setPendingValue('');
    };

    const handleDelete = async (core: Core, optionToDelete: string) => {
        // Prevent deletion of locked remarks
        if (lockedRemarks.includes(optionToDelete)) {
            return;
        }
        setKickoffOptions((prev) => prev.filter((opt) => opt !== optionToDelete));
        if (kickoff === optionToDelete) setKickoff('');
        await Metadata.deleteItem(core, 'finance/travel-kickoff', optionToDelete);
        const updated = await Metadata.retrieveMetadata(core);
        setMetadata(updated);
    };

    return (
        <>
            <Grid xs={6} md={6}>
                <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                    <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>Remark</FormLabel>

                    <Autocomplete
                        freeSolo
                        value={kickoff}
                        inputValue={inputValue}
                        onInputChange={(_, val) => setInputValue(val)}
                        onChange={(_, newValue) => {
                            if (newValue === '__add_new__') {
                                setOpenModal(true);
                            } else if (typeof newValue === 'string') {
                                setKickoff(newValue);
                            } else {
                                setKickoff('');
                            }
                        }}
                        options={kickoffOptions}
                        filterOptions={(options) => {
                            const filtered = options.filter((opt) =>
                                opt.toLowerCase().includes(inputValue.toLowerCase())
                            );
                            return [...filtered, '__add_new__'];
                        }}
                        getOptionLabel={(option) =>
                            option === '__add_new__' ? '➕ Add new…' : option
                        }
                        renderOption={(props, option) => {
                            if (option === '__add_new__') {
                                return (
                                    <li
                                        {...props}
                                        key="add-new"
                                        style={{
                                            padding: '8px 12px',
                                            fontSize: '14px',
                                            lineHeight: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            opacity: 0.8,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ➕ Add new…
                                    </li>
                                );
                            }

                            const isLocked = lockedRemarks.includes(option);

                            return (
                                <li
                                    {...props}
                                    key={option}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        lineHeight: 1.5,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <span
                                        style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            flexGrow: 1,
                                        }}
                                    >
                                        {option}
                                    </span>
                                    {!isLocked && (
                                        <Tooltip title="Delete item" placement="right" variant="outlined">
                                            <IconButton
                                                size="sm"
                                                color="danger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    core ? handleDelete(core, option) : null;
                                                }}
                                                sx={{
                                                    marginLeft: '8px',
                                                    '--IconButton-size': '24px',
                                                    '--Icon-fontSize': '16px',
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </li>
                            );
                        }}
                        startDecorator={
                            <Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>Remark</Chip>
                        }
                    />
                </FormControl>
            </Grid>

            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <ModalDialog>
                    <Typography level="h4">Add New Kick-Off Option</Typography>
                    <Input
                        placeholder="Enter new kick-off"
                        value={pendingValue}
                        onChange={(e) => setPendingValue(e.target.value)}
                        autoFocus
                    />
                    <Button
                        onClick={handleAddNew}
                        disabled={!pendingValue.trim()}
                        sx={{ mt: 2 }}
                    >
                        Add
                    </Button>
                </ModalDialog>
            </Modal>
        </>
    );
};

export default KickoffSelector;
